/**
 * MobileDenuncias.js - Portal de Denúncias Anônimas (App Mobile)
 * Permite funcionários fazerem denúncias de forma anônima
 * A empresa NÃO sabe quem fez a denúncia
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  ArrowLeft,
  Shield,
  ShieldCheck,
  AlertTriangle,
  Send,
  Search,
  Clock,
  CheckCircle,
  MessageSquare,
  Lock,
  Eye,
  EyeOff,
  FileText,
  Copy,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Tipos de denúncia
const TIPOS_DENUNCIA = [
  { value: 'assedio_moral', label: 'Assédio Moral', icon: '😢', desc: 'Humilhação, intimidação, constrangimento' },
  { value: 'assedio_sexual', label: 'Assédio Sexual', icon: '🚫', desc: 'Condutas de natureza sexual indesejadas' },
  { value: 'discriminacao', label: 'Discriminação', icon: '⚖️', desc: 'Raça, gênero, religião, orientação, deficiência' },
  { value: 'fraude', label: 'Fraude/Corrupção', icon: '💰', desc: 'Desvio, propina, falsificação' },
  { value: 'seguranca', label: 'Segurança do Trabalho', icon: '⚠️', desc: 'Riscos, acidentes, falta de EPIs' },
  { value: 'etica', label: 'Violação de Ética', icon: '📋', desc: 'Descumprimento do código de conduta' },
  { value: 'conflito_interesse', label: 'Conflito de Interesse', icon: '🔄', desc: 'Favorecimento, nepotismo' },
  { value: 'outros', label: 'Outros', icon: '📝', desc: 'Outras situações irregulares' }
];

const STATUS_LABELS = {
  pendente: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  em_analise: { label: 'Em Análise', color: 'bg-blue-100 text-blue-700', icon: Eye },
  respondida: { label: 'Respondida', color: 'bg-green-100 text-green-700', icon: MessageSquare },
  arquivada: { label: 'Arquivada', color: 'bg-slate-100 text-slate-700', icon: FileText }
};

export default function MobileDenuncias() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('nova');
  
  // Estado do formulário
  const [tipo, setTipo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [departamento, setDepartamento] = useState('');
  const [dataOcorrencia, setDataOcorrencia] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Estado do código de acompanhamento
  const [codigoAcompanhamento, setCodigoAcompanhamento] = useState('');
  const [codigoBusca, setCodigoBusca] = useState('');
  const [denunciaEncontrada, setDenunciaEncontrada] = useState(null);
  const [buscando, setBuscando] = useState(false);
  
  // Modal de sucesso
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [copiado, setCopiado] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!tipo) {
      toast.error('Selecione o tipo de denúncia');
      return;
    }
    if (!descricao || descricao.trim().length < 20) {
      toast.error('Descreva a situação com pelo menos 20 caracteres');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/api/complaints`, {
        tipo,
        descricao: descricao.trim(),
        departamento: departamento.trim() || null,
        data_ocorrencia: dataOcorrencia || null
      });
      
      setCodigoAcompanhamento(response.data.codigo_acompanhamento);
      setShowSuccessModal(true);
      
      // Limpar formulário
      setTipo('');
      setDescricao('');
      setDepartamento('');
      setDataOcorrencia('');
      
    } catch (error) {
      console.error('Erro ao enviar denúncia:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar denúncia');
    } finally {
      setLoading(false);
    }
  };

  const handleBuscar = async () => {
    if (!codigoBusca || codigoBusca.trim().length < 6) {
      toast.error('Digite o código de acompanhamento completo');
      return;
    }
    
    setBuscando(true);
    setDenunciaEncontrada(null);
    
    try {
      const response = await axios.get(`${API}/api/complaints/track/${codigoBusca.trim().toUpperCase()}`);
      setDenunciaEncontrada(response.data);
    } catch (error) {
      console.error('Erro ao buscar:', error);
      if (error.response?.status === 404) {
        toast.error('Denúncia não encontrada. Verifique o código.');
      } else {
        toast.error('Erro ao buscar denúncia');
      }
    } finally {
      setBuscando(false);
    }
  };

  const copiarCodigo = () => {
    navigator.clipboard.writeText(codigoAcompanhamento);
    setCopiado(true);
    toast.success('Código copiado!');
    setTimeout(() => setCopiado(false), 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-700 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/m')}
            className="text-white hover:bg-slate-800"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-lg font-bold text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-500" />
              Canal de Denúncias
            </h1>
            <p className="text-xs text-slate-400">100% Anônimo e Confidencial</p>
          </div>
        </div>
      </div>

      {/* Aviso de Anonimato */}
      <div className="mx-4 mt-4 bg-green-900/30 border border-green-700 rounded-lg p-3">
        <div className="flex items-start gap-2">
          <ShieldCheck className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-green-400">Sua identidade está protegida</p>
            <p className="text-xs text-green-300/80 mt-1">
              A empresa não terá acesso a nenhuma informação que possa identificá-lo(a). 
              Suas denúncias são completamente anônimas.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 bg-slate-800">
            <TabsTrigger value="nova" className="data-[state=active]:bg-amber-600">
              <Send className="w-4 h-4 mr-2" />
              Nova Denúncia
            </TabsTrigger>
            <TabsTrigger value="acompanhar" className="data-[state=active]:bg-amber-600">
              <Search className="w-4 h-4 mr-2" />
              Acompanhar
            </TabsTrigger>
          </TabsList>

          {/* Tab: Nova Denúncia */}
          <TabsContent value="nova" className="mt-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Tipo de Denúncia */}
              <div>
                <Label className="text-white mb-2 block">Tipo de Denúncia *</Label>
                <div className="grid grid-cols-2 gap-2">
                  {TIPOS_DENUNCIA.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setTipo(t.value)}
                      className={`
                        p-3 rounded-lg border text-left transition-all
                        ${tipo === t.value 
                          ? 'bg-amber-600 border-amber-500 text-white' 
                          : 'bg-slate-800 border-slate-700 text-slate-300 hover:border-slate-600'
                        }
                      `}
                    >
                      <span className="text-lg">{t.icon}</span>
                      <p className="text-sm font-medium mt-1">{t.label}</p>
                      <p className="text-xs opacity-70 mt-0.5 line-clamp-1">{t.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Descrição */}
              <div>
                <Label className="text-white mb-2 block">
                  Descreva a situação *
                </Label>
                <Textarea
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  placeholder="Relate o que aconteceu com o máximo de detalhes possível. Não inclua seu nome ou informações que possam identificá-lo(a)."
                  className="min-h-[150px] bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">
                  Mínimo 20 caracteres. Atual: {descricao.length}
                </p>
              </div>

              {/* Departamento (opcional) */}
              <div>
                <Label className="text-white mb-2 block">
                  Departamento relacionado (opcional)
                </Label>
                <Input
                  value={departamento}
                  onChange={(e) => setDepartamento(e.target.value)}
                  placeholder="Ex: Financeiro, RH, Operações..."
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                />
              </div>

              {/* Data aproximada (opcional) */}
              <div>
                <Label className="text-white mb-2 block">
                  Data aproximada do ocorrido (opcional)
                </Label>
                <Input
                  type="date"
                  value={dataOcorrencia}
                  onChange={(e) => setDataOcorrencia(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>

              {/* Botão de envio */}
              <Button
                type="submit"
                disabled={loading || !tipo || descricao.length < 20}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white h-12"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Enviando...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    Enviar Denúncia Anônima
                  </span>
                )}
              </Button>
            </form>
          </TabsContent>

          {/* Tab: Acompanhar */}
          <TabsContent value="acompanhar" className="mt-4">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Search className="w-4 h-4 text-amber-500" />
                  Acompanhar Denúncia
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-slate-300 mb-2 block">
                    Código de Acompanhamento
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      value={codigoBusca}
                      onChange={(e) => setCodigoBusca(e.target.value.toUpperCase())}
                      placeholder="Ex: DN1A2B3C4D"
                      className="bg-slate-900 border-slate-600 text-white uppercase"
                    />
                    <Button
                      onClick={handleBuscar}
                      disabled={buscando}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      {buscando ? '...' : 'Buscar'}
                    </Button>
                  </div>
                </div>

                {/* Resultado da busca */}
                {denunciaEncontrada && (
                  <div className="bg-slate-900 rounded-lg p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400 text-sm">Status:</span>
                      <Badge className={STATUS_LABELS[denunciaEncontrada.status]?.color}>
                        {STATUS_LABELS[denunciaEncontrada.status]?.label || denunciaEncontrada.status}
                      </Badge>
                    </div>
                    
                    <div>
                      <span className="text-slate-400 text-sm">Tipo:</span>
                      <p className="text-white font-medium">{denunciaEncontrada.tipo}</p>
                    </div>
                    
                    <div>
                      <span className="text-slate-400 text-sm">Data do Registro:</span>
                      <p className="text-white">
                        {new Date(denunciaEncontrada.data_registro).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                    </div>

                    {/* Respostas */}
                    {denunciaEncontrada.respostas && denunciaEncontrada.respostas.length > 0 && (
                      <div>
                        <span className="text-slate-400 text-sm block mb-2">
                          Respostas da Empresa ({denunciaEncontrada.respostas.length}):
                        </span>
                        <div className="space-y-2">
                          {denunciaEncontrada.respostas.map((resp, i) => (
                            <div key={i} className="bg-slate-800 rounded-lg p-3 border-l-2 border-amber-500">
                              <p className="text-white text-sm">{resp.mensagem}</p>
                              <p className="text-slate-500 text-xs mt-2">
                                {new Date(resp.data).toLocaleDateString('pt-BR')}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {(!denunciaEncontrada.respostas || denunciaEncontrada.respostas.length === 0) && (
                      <div className="text-center py-4">
                        <Clock className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                        <p className="text-slate-500 text-sm">Aguardando resposta da empresa</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal de Sucesso */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-400">
              <CheckCircle className="w-6 h-6" />
              Denúncia Registrada
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-slate-300 text-sm">
              Sua denúncia foi registrada de forma <strong className="text-green-400">completamente anônima</strong>. 
              A empresa não terá acesso à sua identidade.
            </p>
            
            <div className="bg-slate-900 rounded-lg p-4">
              <Label className="text-slate-400 text-xs block mb-2">
                Código de Acompanhamento
              </Label>
              <div className="flex items-center gap-2">
                <code className="text-2xl font-mono text-amber-400 flex-1">
                  {codigoAcompanhamento}
                </code>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={copiarCodigo}
                  className="text-slate-400 hover:text-white"
                >
                  {copiado ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                </Button>
              </div>
            </div>
            
            <div className="bg-amber-900/30 border border-amber-700 rounded-lg p-3">
              <p className="text-amber-300 text-xs">
                <strong>Importante:</strong> Guarde este código! Você precisará dele para acompanhar 
                as respostas da empresa. Não é possível recuperá-lo depois.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              onClick={() => {
                setShowSuccessModal(false);
                setActiveTab('acompanhar');
                setCodigoBusca(codigoAcompanhamento);
              }}
              className="w-full bg-amber-600 hover:bg-amber-700"
            >
              Entendi, guardar código
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
